import React, { useContext, useState } from 'react';
import moment from 'moment';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { LegacyEmployee } from 'src/interfaces/api/Employee';
import { toQueryParamsString } from 'src/utils/ApiUtils';
import useAuth from 'src/hooks/api/useAuth';

type GetEmployeeStatsResponseType = Promise<GetEmployeeStatsReponse & { employee: LegacyEmployee }>;

interface EmployeeStats {
  employeesIds: LegacyEmployee['id'][];
  startDate: string | null;
  endDate: string | null;
  setStartDate: (date: string | null) => void;
  setEndDate: (date: string | null) => void;
  addEmployeeId: (employeeId: LegacyEmployee['id']) => void;
  removeEmployeeId: (employeeId: LegacyEmployee['id']) => void;
  employeesStats: Record<LegacyEmployee['id'], GetEmployeeStatsReponse>;
  fetchEmployeeStats: (
    employeeId: LegacyEmployee['id'],
    startDate: string,
    endDate: string
  ) => GetEmployeeStatsResponseType;
}

export interface GetEmployeeStatsReponse {
  appointmentDoneCount?: string;
  customersCreatedCount?: string;
  dealsCreatedCount?: string;
  dealsCreatedPDC?: string;
  dealsWonCount?: string;
  dealsWonPDC?: string;
  dealsLostCount?: string;
}

const Context = React.createContext<EmployeeStats | undefined>(undefined);

const EmployeeStatsProvider: React.FC = (props) => {
  const { fetch } = useAuth();

  const [employeesIds, setEmployeesIds] = useLocalStorage<LegacyEmployee['id'][]>('stats_employees_report', []);
  const [employeesStats, setEmployeesStats] = useState<Record<LegacyEmployee['id'], GetEmployeeStatsReponse>>({});

  const [startDate, setStartDate] = useState<string | null>(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string | null>(moment().endOf('month').format('YYYY-MM-DD'));

  const fetchEmployeeStats = (
    employeeId: LegacyEmployee['id'],
    startDate: string,
    endDate: string
  ): GetEmployeeStatsResponseType => {
    return fetch(`/api/stats-employees/${employeeId}/reports${toQueryParamsString({ startDate, endDate })}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    })
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        }

        throw new Error('Echec lors de la récupération des stats');
      })
      .then((response) => {
        const { employee, ...stats } = response;
        setEmployeesStats((es) => ({ ...es, [employee.id]: stats }));

        return response;
      });
  };

  const addEmployeeId = (employeeId: LegacyEmployee['id']) => {
    if (!employeesIds.includes(employeeId)) {
      setEmployeesIds([...employeesIds, employeeId]);
    }
  };

  const removeEmployeeId = (employeeId: LegacyEmployee['id']) => {
    const newState = { ...employeesStats };
    delete newState[employeeId];

    setEmployeesStats(newState);
    setEmployeesIds(employeesIds.filter((id) => id !== employeeId));
  };

  const stats: EmployeeStats = {
    employeesIds,
    employeesStats,
    startDate,
    endDate,
    setEndDate,
    setStartDate,
    addEmployeeId,
    removeEmployeeId,
    fetchEmployeeStats,
  };

  return <Context.Provider value={stats}>{props.children}</Context.Provider>;
};

const useEmployeeStats = (): EmployeeStats => {
  const value = useContext(Context);

  if (value === undefined) {
    throw new Error('You must wrap components that call `useEmployeeStats` by a `EmployeeStatsProvider`');
  }

  return value;
};

export { EmployeeStatsProvider };
export default useEmployeeStats;
